import { setProperties } from '@ember/object';
import { service } from '@ember/service';

import type { RecordData, RequestError, Service } from 'webapp-types';
import {
	AiSummaryContextLevel,
	AiSummaryLength,
	BlockVisualization,
	GranulationTypeId,
	TimeUnit,
} from 'webapp/enums/enums';
import { hashCode } from 'webapp/utils/number';

import { AiSummaryContentStatus } from 'webapp/components/block/content/ai-summary';

import type { AnyVisualization } from 'webapp/models-custom/block-setup';
import DateRange from 'webapp/models-custom/date-range';
import VisualizationBase from 'webapp/models-custom/visualization/base';

export const Defaults = {
	aiSummaryShowTrend: true,
	aiSummaryLanguage: 'en-us',
	aiSummaryContextLevelType: AiSummaryContextLevel.BALANCED,
	aiSummaryLengthType: AiSummaryLength.NORMAL,
	aiSummaryEditedBy: null,
	aiSummaryEditedOn: null,
	aiSummaryStatus: AiSummaryContentStatus.LOADING,
};

const RecordLimit = 10;

export default class VisualizationAiSummary extends VisualizationBase {
	@service('request') declare requestService: Service.Request;
	@service('store') declare storeService: Service.Store;
	@service('popup') declare popupService: Service.Popup;

	visualizationType = BlockVisualization.AI_SUMMARY;

	recordLimit = RecordLimit;

	get shownText() {
		return this.block.shownText;
	}

	get showTrend() {
		// don't show trend if there's no text / is loading
		return this.block.aiSummaryShowTrend && this.shownText;
	}

	showRegenerationWarning(onConfirm: () => void, onReject: () => void): void {
		if (this.block.aiSummaryIsEdited) {
			this.popupService.openConfirmPopup({
				title: 'Are you sure you want to regenerate this summary?',
				text: 'The action you want to perform will replace your current summary and any edits with a newly generated version. This action cannot be undone.',
				confirmAction: 'Regenerate',
				cancelAction: 'Cancel',
				isWarning: true,
				onConfirm,
				onReject,
			});
		} else {
			onConfirm();
		}
	}

	// --------- Add metrics from board to this block (special) ---------

	addBoardMetricsToBlock() {
		const block = this.block;
		const defaultDateRange = DateRange.createLastX(30, TimeUnit.DAY, GranulationTypeId.TOTAL);
		const dateRange = block.boardObj?.commonBlockDateRange || defaultDateRange;

		const payload = {
			dateRange: {
				range: dateRange.rangeFormula,
				granulation: defaultDateRange.granulationFormula,
			},
		};
		return new Promise((resolve, reject) => {
			this.requestService
				.api<RecordData.Payload>(
					'PUT',
					`/blocks/${block.id}/data-selects/recreate`,
					payload,
				)
				.then((reply) => {
					if (reply) {
						this.storeService.pushPayload(reply);
					}
					resolve(reply);
				})
				.catch((errorData: RequestError) => {
					this.requestService.handleGenericThrownError(errorData);
					reject(errorData);
				});
		});
	}

	// ----------- Detecting possible rendering changes -----------

	override get visualizationSettingsHash(): number {
		return hashCode(
			JSON.stringify({
				t: this.showTrend,
			}),
		);
	}

	// ------- visualization switch helpers --------

	override preCleanupForSwitchTo(visualizationTo: AnyVisualization): void {
		super.preCleanupForSwitchTo(visualizationTo);

		this.loggerService.logToConsole('[Vis change] from Ai Summary: clear specific stuff');

		this.cleanupBlock();
	}

	override updateForSwitchFrom(visualizationFrom: AnyVisualization): void {
		super.updateForSwitchFrom(visualizationFrom);

		this.setDefaultsOnBlock();
	}

	private cleanupBlock() {
		this.block.aiSummaryShowTrend = false;
		this.block.aiSummaryLanguage = '';
		this.block.aiSummaryContextLevelType = null;
		this.block.aiSummaryLengthType = null;
		this.block.aiSummaryEditedBy = null;
		this.block.aiSummaryEditedOn = null;
	}

	private setDefaultsOnBlock() {
		setProperties(this.block, Defaults);
	}

	static override recordLimit = RecordLimit;
}
